import type { DialogProps as LegacyDialogProps } from './Dialog';
import type { Breakpoint } from '@mui/material';
import type { DialogProps, DialogSize } from '@natter-co/design-system';

/**
 * Maps the legacy MUI `Dialog` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at 40 call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS dialog cannot express this one" and sends the caller back to MUI.
 * That is deliberate: a silent best-effort mapping would ship dialogs that look or behave subtly
 * wrong with nothing to catch it at review time. Every `null` below is a design question that has to
 * be answered before those call sites can be refreshed.
 *
 * Note this maps the CHROME only. The header, body and footer are assembled by the wrapper, because
 * the footer is JSX built from the same legacy `Button`s — which refresh themselves through their own
 * wrapper — and a props mapper has no business building elements.
 */
type DsProps = Pick<
  DialogProps,
  | 'size'
  | 'title'
  | 'hideHeader'
  | 'bodyPadding'
  | 'closeLabel'
  | 'role'
  | 'fullScreen'
  | 'showCloseButton'
  | 'closeOnEscape'
  | 'closeOnBackdropClick'
  | 'finalFocus'
  | 'initialFocus'
  | 'data-testid'
>;

/**
 * Mapped by RESOLVED WIDTH, not by name — the same discipline `Buttons/Button/toDsProps` applies to
 * button heights.
 *
 * The widths come from two places. `common/theme/components/MuiDialog.ts` overrides `xs`/`sm`/`md`/`lg`
 * to 400/600/752/968; `small` and `page` are custom breakpoints (`common/theme/breakpoints.ts`) with
 * no override, so MUI resolves them straight from their own values, 400 and 800.
 *
 *   xs    400 -> sm  360   (-40)
 *   small 400 -> sm  360   (-40)
 *   sm    600 -> md  600   (exact)
 *   md    752 -> md  600   (-152)
 *   page  800 -> lg  900   (+100)
 *   lg    968 -> lg  900   (-68)
 *
 * Four legacy widths do not fit three DS cells, and `md` — the WRAPPER'S DEFAULT, so the commonest
 * size in the app — is the one with no counterpart. Collapsing it down to 600 rather than up to 900
 * is a product decision taken for HNS-363, not an approximation this mapper reached on its own: it
 * keeps every dialog nearer its current width at the cost of a 152px narrowing on fifteen of them.
 * The alternative considered was 752 -> 900.
 *
 * The breakpoints deliberately absent are the ones no dialog uses: `mobile` (0), `hug` (584),
 * `tablet` (1024), `desktop` (1440), `xl` (1504). A new call site reaching for one gets MUI rather
 * than a rounded-off width.
 */
const SIZE: Partial<Record<Breakpoint, DialogSize>> = {
  xs: 'sm',
  small: 'sm',
  sm: 'md',
  md: 'md',
  page: 'lg',
  lg: 'lg',
};

/**
 * Cases the DS dialog cannot express, all of which stay on MUI:
 *
 *   - no `title` — DS requires it, because the title IS the dialog's accessible name. A titleless
 *     dialog is announced as "dialog" and nothing else, so this is not a cell to invent.
 *   - a `role` other than `dialog` or `alertdialog`.
 *   - `sx` — an Emotion prop with no effect on a Tailwind popup. Accepting it would silently drop the
 *     styling rather than fail. Call sites that need one branch to the DS dialog themselves until
 *     the flag retires (HNS-264).
 *   - a `maxWidth` outside the scale, including `false` ("no maximum"). See `SIZE`.
 *
 * Mapped rather than refused, since the DS dialog grew the cells for them:
 *
 *   - `role="alertdialog"` and `fullScreen`.
 *   - `subtitle` and a non-empty `shareOptions`: not DS props. The wrapper folds the first into
 *     `description` and renders the second as children, so the mapper has nothing to carry.
 *
 * Four props are IGNORED rather than refused, because under the refresh they have nothing left to
 * express:
 *
 *   - `scroll` — MUI's choice between scrolling the paper and scrolling the body. The DS dialog owns
 *     a single bounded scroll region, and that is the whole point of the redesign (HNS-337): the
 *     header and footer are siblings of the scroll area so the actions cannot scroll away. There is
 *     no second behaviour to choose.
 *   - `fullWidth` — MUI's meaning is "take 100% up to maxWidth", and every DS cell is already a
 *     fixed width. Refusing it would park twenty call sites on MUI over a no-op.
 *   - `centered` / `variant="confirmation"` — the published set draws a left-aligned heading above
 *     left-aligned body copy in every one of its six cells, so centring is not a cell the DS
 *     dialog has. This was a refusal until HNS-367, on the grounds that centring is "a layout nobody
 *     has drawn" — but that reads the absence backwards. The design system has drawn the alignment
 *     it wants and it is not this one, so the seven centred call sites are not waiting on a missing
 *     cell; they are waiting to be left-aligned like everything else. Dropping the centring is the
 *     answer to that design question, not an approximation of it.
 *     TODO(HNS-264): the prop itself folds away with the flag.
 */
export const toDsProps = ({
  title,
  hideHeader,
  bodyPadding,
  closeLabel,
  maxWidth = 'md',
  fullScreen,
  disableRestoreFocus,
  initialFocus,
  finalFocus,
  role,
  sx,
  showCloseButton = true,
  disableClickOutside = false,
  'data-testid': testId,
}: LegacyDialogProps): DsProps | null => {
  if (sx || (role && role !== 'dialog' && role !== 'alertdialog')) {
    return null;
  }

  if (title === undefined) {
    return null;
  }

  const size = maxWidth === false ? undefined : SIZE[maxWidth];

  if (!size) {
    return null;
  }

  return {
    size,
    title,
    hideHeader,
    bodyPadding,
    closeLabel,
    // Left off for a plain dialog so Base UI's own role stays in force.
    role: role === 'alertdialog' ? 'alertdialog' : undefined,
    fullScreen,
    showCloseButton,
    // One legacy prop, two DS ones: the wrapper wires `disableClickOutside` to MUI's
    // `disableEscapeKeyDown` too, so it has always meant "this dialog must be answered" rather than
    // only "ignore the backdrop". Always passed explicitly, so an `alertdialog` keeps the
    // dismissibility its call site has today instead of taking the DS alert default.
    closeOnEscape: !disableClickOutside,
    closeOnBackdropClick: !disableClickOutside,
    /**
     * `false` is Base UI's "do not move focus" — the direct counterpart of MUI's
     * `disableRestoreFocus`, and the reason this is a mapping rather than a refusal.
     *
     * Otherwise the caller's own `finalFocus`, or `undefined` so the DS default (focus back to the
     * trigger, or to whatever was focused before) stays in force. The five call sites that
     * DO pass it are all confirmations opened from a menu item that unmounts with the menu, so
     * restoring focus to it would land nowhere — see `common/components/Menu/Menu.tsx`.
     */
    finalFocus: disableRestoreFocus ? false : finalFocus,
    initialFocus,
    'data-testid': testId,
  };
};
